<template>
  <div class="document-status-dropdown">
    <document-status-panel
      :digitalization-document="digitalizationDocument"
      :file="file"
      :mode="mode"
    ></document-status-panel>

    <inspector-panel
      v-if="isSupplierProject() && isFormalControlDone()"
      :file="file!"
      @status-updated="emitDocumentDataChangedEvent"
    />

    <quality-check-panel
      v-if="
        isDigitalizationProject() &&
        isQualityCheck() &&
        isUserRole(qualityCheckPanelAllowedRoles) &&
        !isSharePointUploadFailed()
      "
      :digitalization-document="digitalizationDocument!"
      @document-data-changed="emitDocumentDataChangedEvent"
    ></quality-check-panel>

    <accept-metadata-inspection-button
      v-if="
        isUserRole(metadataInspectionAllowedRoles) && isMetadataInspection()
      "
      :digitalization-document="digitalizationDocument"
      @document-data-changed="emitDocumentDataChangedEvent"
    ></accept-metadata-inspection-button>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useRoles } from '@/composables/useRoles';
  import DocumentStatusPanel from '@/components/DocumentStatusPanel/DocumentStatusPanel.vue';
  import InspectorPanel from '@/components/InspectorPanel/InspectorPanel.vue';
  import QualityCheckPanel from '@/components/QualityCheckPanel/QualityCheckPanel.vue';
  import AcceptMetadataInspectionButton from '@/components/AcceptMetadataInspectionButton/AcceptMetadataInspectionButton.vue';
  import {
    ProjectType,
    DocumentStatus,
    positiveFormalControlStatuses,
    Role,
  } from 'types';
  import { DocumentStatusDropdownProps } from './DocumentStatusDropdown.types';

  const props = defineProps<DocumentStatusDropdownProps>();

  const emit = defineEmits<{
    (e: 'dropdown-update'): void;
  }>();

  const { isUserRole } = useRoles();

  const metadataInspectionAllowedRoles = ref([
    Role.Admin,
    Role.DocuResponsible,
    Role.DigitalizationCoworker,
  ]);
  const qualityCheckPanelAllowedRoles = ref([Role.Admin, Role.DocuResponsible]);

  function isSupplierProject() {
    return props.mode === ProjectType.SUPPLIER_PROJECT;
  }

  function isDigitalizationProject() {
    return props.mode === ProjectType.DIGITALIZATION_PROJECT;
  }

  function isSharePointUploadFailed(): boolean {
    return (props.file ?? props.digitalizationDocument)!
      .didSharePointUploadFail;
  }

  function isQualityCheck(): boolean {
    return (
      props.digitalizationDocument?.documentProcessingStatus ===
      DocumentStatus.QUALITY_CHECK
    );
  }

  function isMetadataInspection(): boolean {
    return (
      props.digitalizationDocument?.documentProcessingStatus ===
      DocumentStatus.METADATA_INSPECTION
    );
  }

  function isFormalControlDone(): boolean {
    return (
      !!props.file?.formalControlStatus &&
      positiveFormalControlStatuses.includes(props.file?.formalControlStatus)
    );
  }

  function emitDocumentDataChangedEvent(): void {
    emit('dropdown-update');
  }
</script>

<style lang="scss" scoped>
  .document-status-dropdown {
    padding: 1rem 0.5rem;
    width: 400px;
    display: flex;
    flex-direction: column;
  }
</style>
